import React, { KeyboardEvent, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import clsx from 'clsx';
import { HeaderDataItem } from '@interfaces/models/header';
import Link from '@components/atoms/link/link';
import dynamic from 'next/dynamic';
import styles from '../main-navigation.module.scss';
import NewHomepagesEntry from './new-homepages-entry';
const DesktopPopupColumn = dynamic(() => import('@components/molecules/main-nav/components/desktop-popup-column'), {
  ssr: false,
});

interface DesktopItemProps {
  headerDataItem: HeaderDataItem;
  show: boolean;
  onSelect: (id: number | null) => void;
  onClick: (label: string) => void;
  isMainNavigationOpen: boolean;
  setIsMainNavigationOpen: (isOpened: boolean) => void;
  isShoppingPrefsEnabled?: boolean;
}

const DesktopItem: React.FC<DesktopItemProps> = ({
  headerDataItem,
  show,
  onSelect,
  onClick,
  isMainNavigationOpen,
  setIsMainNavigationOpen,
  isShoppingPrefsEnabled = false,
}) => {
  const hasChildren = !!headerDataItem?.content?.length;
  const isLink = headerDataItem?.url?.seo !== '#';
  const [isOpen, setIsOpen] = useState(false);
  const nodeRef = React.useRef(null);
  const headerWrapperRef = useRef<HTMLElement | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debounceCallback = (func: () => void, delay: number) => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(func, delay);
  };

  const handleMouseEnter = () => {
    const openItem = () => {
      onSelect(headerDataItem.id);
      setIsOpen(true);
    };
    if (isMainNavigationOpen) {
      openItem();
      return;
    }
    debounceCallback(() => {
      openItem();
      setIsMainNavigationOpen(true);
    }, 300);
  };

  const handleMouseLeave = () => {
    clearTimeout(timeoutRef.current);

    // If focus is inside the dropdown, move it to the header element before closing
    const dropdownElement = nodeRef.current;
    if (
      dropdownElement &&
      document.activeElement &&
      dropdownElement.contains(document.activeElement) &&
      headerWrapperRef.current
    ) {
      const focusableElement = headerWrapperRef.current.firstElementChild as HTMLElement | null;
      if (focusableElement) {
        // For <span> tags with tabIndex="-1", temporarily make them focusable
        const originalTabIndex = focusableElement.getAttribute('tabindex');
        if (originalTabIndex === '-1') {
          focusableElement.setAttribute('tabindex', '0');
        }

        focusableElement.focus();

        // Restore original tabIndex
        if (originalTabIndex === '-1') {
          focusableElement.setAttribute('tabindex', '-1');
        }
      }
    }

    onSelect(null);
    setIsOpen(false);
  };

  // Keyboard a11y (WCAG 2.4.3 Focus Order): when focus enters the trigger, open the
  // dropdown so it is reachable; when focus leaves the entire item, close it; on
  // Escape, close the dropdown and return focus to the trigger so the user isn't
  // stranded inside an invisible popup.
  const handleFocus = () => {
    onSelect(headerDataItem.id);
    if (hasChildren) {
      setIsOpen(true);
      setIsMainNavigationOpen(true);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLLIElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) {
      return;
    }
    onSelect(null);
    setIsOpen(false);
    // Mirror the mouseleave reset so a keyboard focus session doesn't permanently
    // leave `isMainNavigationOpen` true, which would skip the 300ms hover debounce
    // on the next mouse interaction. Sibling items re-set it to true via their
    // own handleFocus when they receive focus next.
    setIsMainNavigationOpen(false);
  };

  const focusTrigger = () => {
    const trigger = headerWrapperRef.current?.firstElementChild as HTMLElement | null;
    trigger?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLLIElement>) => {
    if (e.key === 'Escape' && hasChildren && isOpen) {
      e.stopPropagation();
      setIsOpen(false);
      onSelect(null);
      setIsMainNavigationOpen(false);
      focusTrigger();
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the <li> is a delegation point for Escape bubbling from the trigger and from any link inside the dropdown; keyboard interactivity itself lives on the children
    <li
      key={headerDataItem.id}
      onMouseEnter={handleMouseEnter}
      onFocus={handleFocus}
      onMouseLeave={handleMouseLeave}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={clsx({
        [styles.desktop__noChildren]: !hasChildren,
        [styles['mainNav__item--highlight']]: headerDataItem?.mnemonic === 'sale',
      })}
      data-mnemonic={headerDataItem?.mnemonic}
      data-cy={`navMenuItem-${headerDataItem.id}`}
    >
      <span
        className={styles.desktop__headerWrapper}
        ref={headerWrapperRef}
      >
        {isLink ? (
          <Link
            href={headerDataItem.url.seo}
            tabIndex={0}
            onClick={() => onClick(headerDataItem.localizedName)}
            className={styles.desktop__headerLink}
            aria-haspopup={hasChildren ? true : undefined}
            aria-expanded={hasChildren ? isOpen : undefined}
          >
            {headerDataItem.localizedName}
          </Link>
        ) : (
          <span
            tabIndex={-1}
            className={styles.desktop__headerText}
            aria-haspopup={hasChildren ? true : undefined}
            aria-expanded={hasChildren ? isOpen : undefined}
          >
            {headerDataItem.localizedName}
          </span>
        )}
      </span>
      {/* Popup */}
      {hasChildren && (
        <details
          open={isOpen}
          aria-label={headerDataItem.localizedName}
        >
          <summary>
            <span />
          </summary>
          <CSSTransition
            classNames={{
              appear: styles['fade-enter'],
              appearActive: styles['fade-enter-active'],
              exit: styles['fade-exit'],
              exitActive: styles['fade-exit-active'],
            }}
            nodeRef={nodeRef}
            timeout={300}
            in={show}
            unmountOnExit
            mountOnEnter
          >
            <div
              className={styles.desktop__popup}
              ref={nodeRef}
            >
              <div className="innerContainer">
                {/* Render columns */}
                {isShoppingPrefsEnabled && (
                  <NewHomepagesEntry
                    headerDataItem={headerDataItem}
                    onClick={onClick}
                  />
                )}
                {headerDataItem.content.map((link: HeaderDataItem) => (
                  <DesktopPopupColumn
                    headerDataItem={link}
                    key={link.id}
                    onClick={(label: string) => onClick(`${headerDataItem.localizedName}/${label}`)}
                  />
                ))}
              </div>
            </div>
          </CSSTransition>
        </details>
      )}
    </li>
  );
};

export default DesktopItem;
